import { Coordinate2D, Point2D } from './Elements'
import { ElementsData, Storey, Wall2D } from '../../../models/Construction'

export class ElementConverter {
  static toScreenCoords(
    buildingPoint: Coordinate2D,
    scale: number = 0.5,
    offset: Point2D = { x: 50, y: 50 },
  ): Point2D {
    return {
      x: buildingPoint[0] * scale + offset.x,
      y: buildingPoint[1] * scale + offset.y,
    }
  }

  static wallToLinePoints(
    wall: Wall2D,
    scale: number = 0.5,
    deduct: number = 0,
  ): number[] {
    const { center_point, length, angle } = wall
    const halfLength = length / 2
    const radians = (angle * Math.PI) / 180

    const startX = center_point[0] - deduct - halfLength * Math.cos(radians)
    const startY = center_point[1] - halfLength * Math.sin(radians)
    const endX = center_point[0] + halfLength * Math.cos(radians)
    const endY = center_point[1] - deduct + halfLength * Math.sin(radians)

    return [startX * scale, startY * scale, endX * scale, endY * scale]
  }

  static polygonToPoints(
    polyPoints: Coordinate2D[],
    scale: number = 0.5,
  ): number[] {
    return polyPoints.flatMap((point) => [point[0] * scale, point[1] * scale])
  }

  static calculateBounds(floorPlanData: ElementsData): {
    minX: number
    minY: number
    maxX: number
    maxY: number
    width: number
    height: number
  } {
    let minX = Infinity,
      minY = Infinity
    let maxX = -Infinity,
      maxY = -Infinity

    // Check room bounds
    floorPlanData?.rooms?.forEach((room) => {
      room.floor.poly_points.forEach((point) => {
        minX = Math.min(minX, point[0])
        minY = Math.min(minY, point[1])
        maxX = Math.max(maxX, point[0])
        maxY = Math.max(maxY, point[1])
      })
    })

    // Check wall bounds
    floorPlanData?.walls?.forEach((wall) => {
      const points = this.wallToLinePoints(wall, 1)
      for (let i = 0; i < points.length; i += 2) {
        minX = Math.min(minX, points[i])
        minY = Math.min(minY, points[i + 1])
        maxX = Math.max(maxX, points[i])
        maxY = Math.max(maxY, points[i + 1])
      }
    })

    return {
      minX,
      minY,
      maxX,
      maxY,
      width: maxX - minX,
      height: maxY - minY,
    }
  }

  static convertStoreyDataToElementsData(
    storeyArrData: Storey[],
  ): ElementsData | null {
    if (!storeyArrData) return null
    const elementsData: ElementsData = {
      id: storeyArrData[0].id,
      name: storeyArrData[0].name,
      height: storeyArrData[0].height,
      // rooms: storeyData.rooms.map(room => ({
      //   id: room.id,
      //   name: room.name,
      //   floor: {
      //     poly_points: room.floor.poly_points,
      //     storey_id: room.storey_id,
      //     elevation: room.floor.elevation,
      //   },
      //   walls: room.walls.map(wall => ({
      //     id: wall.id,
      //     center_point: wall.center_point,
      //     length: wall.length,
      //     angle: wall.angle
      //   })),
      //   windows: room.windows.map(window => ({
      //     id: window.id,
      //     center_location_on_wall: window.center_location_on_wall,
      //     width: window.width
      //   })),
      //   doors: room.doors.map(door => ({
      //     id: door.id,
      //     center_location_on_wall: door.center_location_on_wall,
      //     width: door.width
      //   }))
      // })),
      walls: storeyArrData.flatMap(storeyData => storeyData.walls).map((wall) => ({
        id: wall.id,
        center_point: wall.center_point as [number, number],
        length: wall.length,
        angle: wall.angle,
        height: wall.height,
        thickness: wall.thickness,
        windows: wall.windows.map((window) => ({
          id: window.id,
          center_location_on_wall: window.center_location_on_wall,
          width: window.width,
          type: window.type,
          height: window.height,
          thickness: window.thickness,
          sill_height: window.sill_height,
          studs: window.studs.map((stud) => ({
            id: stud.id,
            center_point: stud.center_point,
            length: stud.length,
            rotation_x: stud.rotation_x,
            rotation_y: stud.rotation_y,
            rotation_z: stud.rotation_z,
            width: stud.width,
            thickness: stud.thickness,
            type: 'Stud',
          })),
          headers: window.headers.map((header) => ({
            id: header.id,
            center_point: header.center_point,
            length: header.length,
            rotation_x: header.rotation_x,
            rotation_y: header.rotation_y,
            rotation_z: header.rotation_z,
            width: header.width,
            thickness: header.thickness,
            type: 'Header',
          })),
        })),
        doors: wall.doors.map((door) => ({
          id: door.id,
          center_location_on_wall: door.center_location_on_wall,
          width: door.width,
          type: door.type,
          height: door.height,
          thickness: door.thickness,
          sill_height: door.sill_height,
        })),
        type: 'Wall',
      })),
      beams: storeyArrData.flatMap(storeyData => storeyData.beams).map((beam) => ({
        id: beam.id,
        center_point: beam.center_point,
        length: beam.length,
        rotation_x: beam.rotation_x,
        rotation_y: beam.rotation_y,
        rotation_z: beam.rotation_z,
        width: beam.width,
        thickness: beam.thickness,
        type: 'Beam',
      })),
      spans: storeyArrData.flatMap(storeyData => storeyData.spans).map((span) => ({
        id: span.id,
        joists: span.joists.map((joist) => ({
          id: joist.id,
          center_point: joist.center_point,
          length: joist.length,
          rotation_x: joist.rotation_x,
          rotation_y: joist.rotation_y,
          rotation_z: joist.rotation_z,
          width: joist.width,
          thickness: joist.thickness,
          type: 'Joist',
        })),
      })),
      load_bearing_walls: storeyArrData.flatMap(storeyData => storeyData.load_bearing_walls).map((wall) => ({
        id: wall.id,
        center_point: wall.center_point as [number, number],
        length: wall.length,
        angle: wall.angle,
        height: wall.height,
        thickness: wall.thickness,
        type: 'Load Bearing Wall',
      })),
      non_bearing_walls: storeyArrData.flatMap(storeyData => storeyData.non_bearing_walls).map((wall) => ({
        id: wall.id,
        center_point: wall.center_point as [number, number],
        length: wall.length,
        angle: wall.angle,
        height: wall.height,
        thickness: wall.thickness,
        type: 'Load Bearing Wall',
      })),
      optional_shear_walls: storeyArrData.flatMap(storeyData => storeyData.optional_shear_walls).map((wall) => ({
        id: wall.id,
        center_point: wall.center_point as [number, number],
        length: wall.length,
        angle: wall.angle,
        height: wall.height,
        thickness: wall.thickness,
        type: 'Shear Wall',
      })),
      selected_shear_walls: storeyArrData.flatMap(storeyData => storeyData.selected_shear_walls).map((wall) => ({
        id: wall.id,
        center_point: wall.center_point as [number, number],
        length: wall.length,
        angle: wall.angle,
        height: wall.height,
        thickness: wall.thickness,
        type: 'Shear Wall',
      })),
    }
    return elementsData
  }
}
